export const nest = (items: Array<any>, id = 0, link = 'RefId'): any => {
  return items
    .filter((item) => item[link] === id)
    .map((item: any) => {
      const children = nest(items, item.Id);
      return {
        ...item,
        value: item.Id,
        label: item.Text,
        children: children.length ? children : null,
      };
    });
};

export const chunkArray = (array, size: number) => {
  let filteredArray = Array.isArray(array)
    ? array.filter((item) => item.trim() !== '')
    : array.split(',').filter((item) => item.trim() !== '');
  let result = [];
  for (let i = 0; i < filteredArray.length; i += size) {
    let chunk = filteredArray.slice(i, i + size);
    while (chunk.length < size) {
      chunk.push('');
    }
    result.push(chunk);
  }
  return result;
};

export const arrayToObject = <K extends string, V = string>(
  keys: K[],
  defaultValue?: V | ((key: K) => V)
): Record<K, V> => {
  const getValue = (k: K) =>
    typeof defaultValue === 'function' ? (defaultValue as (k: K) => V)(k) : (defaultValue as V) ?? ('' as unknown as V);
  const result = {} as Record<K, V>;
  for (const k of keys) result[k] = getValue(k);
  return result;
}

export const normalizeDimensionsByColumn = (dimensionArray = []) => {
  if (!Array.isArray(dimensionArray) || dimensionArray.length === 0) return [];

  const mergedByColumn = new Map<string, any>();

  dimensionArray.forEach((dimension: any) => {
    if (!dimension || typeof dimension !== 'object') return;

    const dimensionId = Number(dimension?.DimensionId || 0);
    const dimensionColumn = String(dimension?.DimensionColumn || '').trim();
    if (!dimensionId || dimensionColumn === '') return;

    const key = `${dimensionId}__${dimensionColumn.toLowerCase()}`;
    const values = Array.isArray(dimension?.DimensionValues)
      ? dimension.DimensionValues.map((value: any) => String(value ?? '-'))
      : [];

    if (!mergedByColumn.has(key)) {
      mergedByColumn.set(key, {
        ...dimension,
        DimensionId: dimensionId,
        DimensionColumn: dimensionColumn,
        DimensionValues: values,
      });
      return;
    }

    const existing = mergedByColumn.get(key);
    const existingValues = Array.isArray(existing?.DimensionValues)
      ? existing.DimensionValues
      : [];
    const maxLength = Math.max(existingValues.length, values.length);

    existing.DimensionValues = Array.from({ length: maxLength }, (_, index) => {
      const oldValue = existingValues[index];
      const newValue = values[index];
      const hasOld =
        oldValue !== undefined &&
        oldValue !== null &&
        String(oldValue).trim() !== '' &&
        String(oldValue).trim() !== '-';
      const hasNew =
        newValue !== undefined &&
        newValue !== null &&
        String(newValue).trim() !== '' &&
        String(newValue).trim() !== '-';

      if (hasOld) return String(oldValue);
      if (hasNew) return String(newValue);
      return '-';
    });

    mergedByColumn.set(key, existing);
  });

  return Array.from(mergedByColumn.values());
};

export const transformDimensionData = (dimensionArray) => {
  const normalizedDimensionArray = normalizeDimensionsByColumn(dimensionArray);

  // Check if input is valid
  if (!Array.isArray(normalizedDimensionArray) || normalizedDimensionArray.length === 0) {
    return [];
  }

  // Find the maximum length of DimensionValues array
  let maxLength = 0;
  for (const dimension of normalizedDimensionArray) {
    if (dimension && dimension.DimensionValues && Array.isArray(dimension.DimensionValues)) {
      maxLength = Math.max(maxLength, dimension.DimensionValues.length);
    }
  }

  if (maxLength === 0) {
    return [];
  }

  // Initialize the result array
  const result = [];

  for (let i = 0; i < maxLength; i++) {
    const obj = {};
    let hasValidData = false;

    for (const dimension of normalizedDimensionArray) {
      // Skip invalid dimension objects
      if (!dimension ||
        typeof dimension !== 'object' ||
        !dimension.DimensionColumn ||
        !Array.isArray(dimension.DimensionValues)) {
        continue;
      }

      const columnName = String(dimension.DimensionColumn);
      const values = dimension.DimensionValues;

      // Get value at index i, or null if index doesn't exist
      const value = i < values.length ? values[i] : null;

      // Only add to object if value exists
      if (value !== null && value !== undefined) {
        obj[columnName] = String(value);
        hasValidData = true;
      }
    }

    // Only add object to result if it has at least one property
    if (hasValidData && Object.keys(obj).length > 0) {
      result.push(obj);
    }
  }

  return result;
}
